import React, { ReactNode } from 'react';
import { AlertTriangle, Info, Scissors } from 'lucide-react';

export type NoticeTone = 'info' | 'capped' | 'warning' | 'error';

const TONE_STYLES: Record<NoticeTone, { wrapper: string; icon: typeof Info }> = {
  info: {
    wrapper:
      'bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-800 dark:text-blue-200',
    icon: Info,
  },
  // `capped` is deliberately its own calm, neutral tone — a capped run is a
  // SUCCESS (the partial answer stands), so it must never look like a failure.
  capped: {
    wrapper:
      'bg-gray-100 border-gray-200 text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-300',
    icon: Scissors,
  },
  warning: {
    wrapper:
      'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-800 dark:text-amber-200',
    icon: AlertTriangle,
  },
  error: {
    wrapper:
      'bg-red-50 border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-200',
    icon: AlertTriangle,
  },
};

interface InlineNoticeProps {
  tone: NoticeTone;
  children: ReactNode;
}

const InlineNotice: React.FC<InlineNoticeProps> = ({ tone, children }) => {
  const { wrapper, icon: Icon } = TONE_STYLES[tone];
  return (
    <div
      className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${wrapper}`}
      role={tone === 'error' ? 'alert' : 'status'}
    >
      <Icon className="w-4 h-4 shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1 break-words">{children}</div>
    </div>
  );
};

export default InlineNotice;
